import React, { useState, useEffect } from 'react';
import './Login.css';
import TruckImage from './Images/TruckImage.png';
import { useNavigate } from 'react-router-dom';

const LoginInput = ({ type, placeholder, value, onChange, onKeyDown }) => (
  <input
    type={type}
    placeholder={placeholder}
    className="login-input"
    value={value}
    onChange={onChange}
    onKeyDown={onKeyDown}
    style={{ width: '100%' }}
  />
);

const Login = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [message, setMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(180);
  const [isResendAllowed, setIsResendAllowed] = useState(false);
  const [step, setStep] = useState('email');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [passwordValidations, setPasswordValidations] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('telnyxToken');
    localStorage.removeItem('user');
    localStorage.removeItem('UIComponents');
    localStorage.removeItem('callScript');
  }, []);

  const backendURL = 'https://salesjet-10097049716.development.catalystappsail.com';

  const validatePasswordLive = (value) => {
    setPasswordValidations({
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value),
    });
  };

  useEffect(() => {
    let timer;
    if (showOtp && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    if (resendTimer === 0) {
      setIsResendAllowed(true);
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [showOtp, resendTimer]);

  const handleResendOtp = async () => {
    try {
      const response = await fetch(`${backendURL}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('OTP resent to your email.');
        setResendTimer(60);
        setIsResendAllowed(false);
      } else {
        setMessage(data.message || 'Failed to resend OTP.');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setMessage('Error resending OTP.');
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setMessage('Please enter email and password');
      return;
    }

    if (!validatePassword(password)) {
      setMessage('Password must be 8+ chars, include uppercase, number & special char.');
      return;
    }

    try {
      const ipRes = await fetch('https://api64.ipify.org?format=json');
      const ipData = await ipRes.json();

      const authResponse = await fetch(`${backendURL}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ipAddress: ipData.ip }),
      });

      const authData = await authResponse.json();
      console.log('Authentication response data:', authData); // Debugging log

      if (authData.requiresOTP) {
        setShowOtp(true);
        setMessage(authData.message);
        return;
      }

      if (authResponse.ok && authData.accessToken) {
        localStorage.setItem('accessToken', authData.accessToken);
        localStorage.setItem('refreshToken', authData.refreshToken);
        localStorage.setItem('telnyxToken', authData.telnyxToken);
        localStorage.setItem('user', JSON.stringify(authData.user));
        localStorage.setItem('Userpreference', authData.Userpreference);
          if (authData.UIComponents) {
            localStorage.setItem('UIComponents', JSON.stringify(authData.UIComponents));
          }
        setMessage('Login successful!');
        navigate('/dashboard');
      } else {
        setMessage(authData.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setMessage('Something went wrong.');
    }
  };

  const handleOtpVerification = async () => {
    try {
      const response = await fetch(`${backendURL}/verify-otp-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();
      console.log('OTP verification response data:', data); // Debugging log
      if (response.ok) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('telnyxToken', data.telnyxToken);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('Userpreference', data.Userpreference);
        if (data.UIComponents) {
          localStorage.setItem('UIComponents', JSON.stringify(data.UIComponents));
        }
        setMessage('Login successful!');
        navigate('/dashboard');
      } else {
        setMessage(data.message || 'OTP verification failed');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setMessage('An error occurred during OTP verification');
    }
  };

  const handleEmailNext = async () => {
    if (!email) return setMessage('Please enter email');

    try {
      const response = await fetch(`${backendURL}/check-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok || !data.exists) {
        setMessage('Invalid user');
        return;
      }

      if (data.hasPassword) {
        setStep('login');
      } else {
        setStep('setPassword');
      }
    } catch (err) {
      console.error('User check error:', err);
      setMessage('Error checking user.');
    }
  };

  const handleSetPassword = async () => {
    if (!password || !confirmPassword || !securityQuestion || !securityAnswer) {
      setMessage('Please fill all fields');
      return;
    }

    if (!validatePassword(password)) {
      setMessage('Password must be 8+ chars, include uppercase, number & special char.');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    try {
      const response = await fetch(`${backendURL}/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          securityQuestion,
          securityAnswer,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password set successfully. Please login.');
        setStep('login');
      } else {
        setMessage(data.message || 'Failed to set password.');
      }
    } catch (err) {
      console.error('Set password error:', err);
      setMessage('Error setting password.');
    }
  };

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    return regex.test(password);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-image-section">
        <img
          src={TruckImage}
          alt="Truck"
          className="truck-image"
          loading="eager"
          fetchpriority="high"
          width="600"
          height="400"
          onLoad={() => setImageLoaded(true)}
        />
        <img
          src="https://allcanadavanlines.com/static/media/ACVLediteddd.bea95372cf56c281932e.png"
          alt="ACVL Logo"
          className="logo"
        />
      </div>

      {!imageLoaded ? (
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="login-form-section">
          <h1 className="app-name">Sales Jet</h1>
          <div className="login-box">
            <h2 className="login-title">Login</h2>
            <p className="login-subtitle">Welcome back</p>
            {step === 'email' && (
              <>
                <LoginInput
                  type="text"
                  placeholder="Email Id"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleEmailNext)} // 👈 Added Enter key handler
                />
                <button className="login-button" onClick={handleEmailNext}>
                  Next
                </button>
              </>
            )}

            {step === 'login' && (
              <>
                <LoginInput
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleLogin)} // 👈 Added Enter key handler
                />
                {!showOtp && (
                  <div
                    className="login-remember"
                    style={{ marginTop: '5px', marginBottom: '15px' }}
                  >
                    <span
                      className="forgot-link"
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '14px',
                      }}
                      onClick={() => navigate('/forgot-password')}
                    >
                      Forgot Password?
                    </span>
                  </div>
                )}
                {!showOtp && (
                  <button className="login-button" onClick={handleLogin}>
                    Login
                  </button>
                )}
              </>
            )}

            {showOtp && (
              <>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    marginTop: '20px',
                  }}
                >
                  <LoginInput
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleOtpVerification)} // 👈 Added Enter key handler
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: '10px',
                      bottom: '-20px',
                      fontSize: '13px',
                    }}
                  >
                    {isResendAllowed ? (
                      <span
                        className="resend-link"
                        style={{
                          color: 'blue',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                        onClick={handleResendOtp}
                      >
                        Resend OTP
                      </span>
                    ) : (
                      <span style={{ color: 'gray' }}>
                        Resend in {Math.floor(resendTimer / 60)}:
                        {(resendTimer % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  className="login-button"
                  onClick={handleOtpVerification}
                  style={{ marginTop: '30px' }}
                >
                  Verify OTP
                </button>
              </>
            )}

            {step === 'setPassword' && (
              <>
                <LoginInput
                  type="password"
                  placeholder="Set Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePasswordLive(e.target.value);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, handleSetPassword)} // 👈 Added Enter key handler
                />
                <div
                  style={{
                    fontSize: '8px',
                    marginTop: '-20px',
                    display: 'flex',
                    marginBottom: '7px',
                  }}
                >
                  <p style={{ color: passwordValidations.length ? 'green' : 'red' }}>
                    • Minimum 8 characters
                  </p>
                  <p style={{ color: passwordValidations.uppercase ? 'green' : 'red' }}>
                    • At least 1 uppercase letter
                  </p>
                  <p style={{ color: passwordValidations.number ? 'green' : 'red' }}>
                    • At least 1 number
                  </p>
                  <p style={{ color: passwordValidations.specialChar ? 'green' : 'red' }}>
                    • At least 1 special character
                  </p>
                </div>
                <LoginInput
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSetPassword)} // 👈 Added Enter key handler
                />
                <select
                  style={{ marginLeft: '0px', minWidth: '347px' }}
                  className="login-input"
                  value={securityQuestion}
                  onChange={(e) => setSecurityQuestion(e.target.value)}
                >
                  <option value="">Select a Security Question</option>
                  <option value="What is your pet's name?">What is your pet's name?</option>
                  <option value="What is your mother's maiden name?">
                    What is your mother's maiden name?
                  </option>
                  <option value="What was your childhood nickname?">
                    What was your childhood nickname?
                  </option>
                  <option value="What was your first school name?">
                    What was your first school name?
                  </option>
                  <option value="What is your favorite food?">What is your favorite food?</option>
                </select>
                <LoginInput
                  type="text"
                  placeholder="Security Answer"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, handleSetPassword)} // 👈 Added Enter key handler
                />
                <button
                  className="login-button"
                  onClick={handleSetPassword}
                  disabled={
                    !passwordValidations.length ||
                    !passwordValidations.uppercase ||
                    !passwordValidations.number ||
                    !passwordValidations.specialChar
                  }
                  style={{
                    backgroundColor:
                      passwordValidations.length &&
                      passwordValidations.uppercase &&
                      passwordValidations.number &&
                      passwordValidations.specialChar
                        ? '#4CAF50'
                        : '#ccc',
                    cursor:
                      passwordValidations.length &&
                      passwordValidations.uppercase &&
                      passwordValidations.number &&
                      passwordValidations.specialChar
                        ? 'pointer'
                        : 'not-allowed',
                  }}
                >
                  Submit
                </button>
                <p style={{ fontSize: '12px', color: '#555' }}>
                  <b>Note :</b>
                </p>
                <p style={{ fontSize: '12px', color: '#555', marginTop: '3px' }}>
                  Password must be at least <b>8 characters</b>, contain at least 
                  <b> 1 uppercase letter</b>, <b>1 number</b> and <b>1 special character</b>.
                </p>
              </>
            )}

            {message && (
              <p className="login-message" style={{ color: 'red', marginTop: '20px' }}>
                {message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;

